/**
 * Wave D — shared outbound fetch that arms HTTP_STORM_DETECTED latch and
 * freezes {@link freezeCustomerOutboundForStorm}. Islands use this instead of
 * raw `fetch` so ApiClient-only wiring is not sufficient Exit theater.
 *
 * G5 — same-origin `/api/*` calls also enqueue through
 * {@link enqueueCustomerOutbound} so HOT_PATH spacing applies without rewriting
 * every island. ApiClient passes {@link StormAwareFetchOptions.skipQueue} after
 * it has already scheduled via the fair-queue (BAN nested enqueue).
 *
 * G7/G8/G10 — guest latch + meAttemptCeiling are SoT absolute refuse for auth-hot
 * `/me` and refresh (outside the fair-queue). Fair-queue is spacing/depth when
 * unlatched; refuse-enqueue / auth-lane freeze / startWaiter re-check → 0 wire
 * while latched.
 */

import { getRetryAfterMs } from 'pi-kiosk-shared';
import {
  enqueueCustomerOutbound,
  freezeCustomerOutboundForStorm,
} from './customerOutboundFairQueue.js';
import {
  getHttpStormRetryAfterMs,
  HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS,
  isHttpStormLatched,
  onHttpStormDetected,
  responseHasHttpStormDetected,
} from './httpStormDetectedLatch.js';

export const HTTP_STORM_LATCHED_CODE = 'HTTP_STORM_LATCHED' as const;

export class HttpStormLatchedError extends Error {
  public readonly code = HTTP_STORM_LATCHED_CODE;
  public readonly status = 429;
  public readonly retryAfterMs: number;

  public constructor(retryAfterMs?: number) {
    const ms =
      retryAfterMs !== undefined && retryAfterMs > 0
        ? retryAfterMs
        : getHttpStormRetryAfterMs() || HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS;
    super(
      // Technical English for logs / diagnostics only — ApiClient remaps to i18n on ApiError.
      `HTTP storm pause active. Retry after ${String(Math.ceil(ms / 1000))}s.`,
    );
    this.name = 'HttpStormLatchedError';
    this.retryAfterMs = ms;
  }
}

export function isHttpStormLatchedError(error: unknown): boolean {
  return error instanceof HttpStormLatchedError;
}

/** Soft-skip before any outbound call while the global latch is armed. */
export function assertNotHttpStormLatched(): void {
  if (isHttpStormLatched()) {
    throw new HttpStormLatchedError();
  }
}

/**
 * Arm (or extend) latch + fair-queue freeze from any response carrying
 * HTTP_STORM_DETECTED (presence is enough; typically paired with 429).
 */
export function armHttpStormLatchFromResponse(response: Response): void {
  if (!responseHasHttpStormDetected(response.headers)) {
    return;
  }
  const retryAfterMs = getRetryAfterMs(
    { response },
    HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS,
  );
  onHttpStormDetected(retryAfterMs / 1000);
  freezeCustomerOutboundForStorm(
    Math.max(retryAfterMs, getHttpStormRetryAfterMs()),
  );
}

/** Optional scheduling controls for {@link stormAwareFetch}. */
export interface StormAwareFetchOptions {
  /**
   * When true, do not enqueue (caller already holds a fair-queue slot — ApiClient).
   * Network work still honors latch pre-flight + arm-on-header.
   */
  readonly skipQueue?: boolean;
}

export function resolveFetchUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

/** Nominatim / blob URLs stay on native fetch (G15 — leave geocode + downloads alone). */
export function shouldBypassCustomerStormFetchGuard(input: RequestInfo | URL): boolean {
  const url = resolveFetchUrlString(input);
  if (url.startsWith('blob:')) {
    return true;
  }
  const lower = url.toLowerCase();
  return lower.includes('nominatim.openstreetmap.org') || lower.includes('/nominatim');
}

/**
 * True when the URL targets our customer/backend API (`/api/…`) and should
 * receive fair-queue HOT_PATH spacing. Excludes blob / Nominatim / non-API hosts.
 */
export function shouldEnqueueStormAwareFetch(input: RequestInfo | URL): boolean {
  if (shouldBypassCustomerStormFetchGuard(input)) {
    return false;
  }
  const raw = resolveFetchUrlString(input);
  if (raw.startsWith('/api/') || raw === '/api' || raw.startsWith('/api?') || raw.startsWith('/api#')) {
    return true;
  }
  try {
    const base =
      typeof globalThis.location?.href === 'string' && globalThis.location.href.length > 0
        ? globalThis.location.href
        : 'http://localhost/';
    const parsed = new URL(raw, base);
    if (parsed.protocol === 'blob:') {
      return false;
    }
    return parsed.pathname === '/api' || parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

function resolveStormAwareMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (typeof init?.method === 'string' && init.method.length > 0) {
    return init.method;
  }
  if (typeof Request !== 'undefined' && input instanceof Request && input.method) {
    return input.method;
  }
  return 'GET';
}

function resolveStormAwarePath(input: RequestInfo | URL): string {
  const raw = resolveFetchUrlString(input);
  if (raw.startsWith('/')) {
    const q = raw.indexOf('?');
    const h = raw.indexOf('#');
    let end = raw.length;
    if (q >= 0) {
      end = Math.min(end, q);
    }
    if (h >= 0) {
      end = Math.min(end, h);
    }
    return raw.slice(0, end);
  }
  try {
    const base =
      typeof globalThis.location?.href === 'string' && globalThis.location.href.length > 0
        ? globalThis.location.href
        : 'http://localhost/';
    return new URL(raw, base).pathname;
  } catch {
    return raw;
  }
}

function resolveStormAwareSignal(
  input: RequestInfo | URL,
  init?: RequestInit,
): AbortSignal | undefined {
  if (init?.signal != null) {
    return init.signal;
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.signal ?? undefined;
  }
  return undefined;
}

/**
 * Wire hop only (HTTP storm pre-flight + arm-on-header).
 * G7 — guest latch is NOT re-checked here: SoT is refuse-enqueue +
 * {@link enqueueCustomerOutbound} `startWaiter` auth-lane re-assert (sync after
 * onStart + Promise job before `run`). skipQueue callers already held that slot.
 */
async function executeStormAwareNetwork(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  assertNotHttpStormLatched();
  const response = await fetch(input, init);
  armHttpStormLatchFromResponse(response);
  return response;
}

/**
 * Drop-in `fetch` for customer API islands: honor latch pre-flight, arm on header,
 * and enqueue same-origin `/api/*` through the customer fair-queue (G5).
 */
export async function stormAwareFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: StormAwareFetchOptions,
): Promise<Response> {
  const skipQueue = options?.skipQueue === true;
  if (skipQueue || !shouldEnqueueStormAwareFetch(input)) {
    return executeStormAwareNetwork(input, init);
  }

  return enqueueCustomerOutbound({
    method: resolveStormAwareMethod(input, init),
    path: resolveStormAwarePath(input),
    signal: resolveStormAwareSignal(input, init),
    run: () => executeStormAwareNetwork(input, init),
  });
}

let customerStormFetchGuardInstalled = false;

/**
 * Install once at customer bootstrap so residual raw `fetch` islands honor
 * HTTP_STORM_DETECTED (kiosk twin). Blob + Nominatim bypass the latch wrap.
 * Does not enqueue (fair-queue lives on {@link stormAwareFetch} to avoid
 * nesting when ApiClient already scheduled).
 */
export function installCustomerHttpStormFetchGuard(): void {
  if (customerStormFetchGuardInstalled) {
    return;
  }
  if (typeof globalThis.fetch !== 'function') {
    return;
  }
  customerStormFetchGuardInstalled = true;
  const nativeFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = (async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    if (shouldBypassCustomerStormFetchGuard(input)) {
      return nativeFetch(input, init);
    }
    assertNotHttpStormLatched();
    const response = await nativeFetch(input, init);
    armHttpStormLatchFromResponse(response);
    return response;
  }) as typeof fetch;
}
